/**
 * Map a passkey verification error to a user-facing message.
 *
 * Used by /login, /step-up, and /security where the silent close on
 * NotAllowedError / wrong-passkey was making real failures look like
 * the page was broken (the bug that surfaced during the Eliza passkey
 * investigation — no feedback when verify failed).
 *
 * Inputs:
 *   - DOMException thrown by navigator.credentials.get() (cancellation,
 *     RPID mismatch, allow-list miss). Identified by `name` property.
 *   - BetterAuthError returned from authClient.signIn.passkey() (server
 *     rejected the assertion). Identified by `message`/`status`.
 *   - Generic unknowns — fall back to a retry message.
 */
export type PasskeyErrorOutcome =
  | { kind: "cancelled"; message: string }
  | { kind: "no-credential"; message: string }
  | { kind: "origin-mismatch"; message: string }
  | { kind: "invalid-state"; message: string }
  | { kind: "server-rejected"; message: string }
  | { kind: "rate-limited"; message: string }
  | { kind: "unknown"; message: string };

const TRY_AGAIN = "Try again or use a different verification method.";

export function classifyPasskeyError(err: unknown): PasskeyErrorOutcome {
  if (err instanceof Error) {
    // WebAuthn-level failures (browser → never reached server).
    switch (err.name) {
      case "NotAllowedError":
        // Covers both user-cancellation and "no matching credential found."
        // Browsers don't distinguish for privacy reasons — we can't tell
        // which happened, so the message has to cover both.
        return {
          kind: "cancelled",
          message: `Passkey verification was cancelled or no matching passkey was found. ${TRY_AGAIN}`,
        };
      case "SecurityError":
        return {
          kind: "origin-mismatch",
          message: `This device or browser isn't recognized for this account. ${TRY_AGAIN}`,
        };
      case "InvalidStateError":
        return {
          kind: "invalid-state",
          message: `This passkey can't be used here — it may already be registered. ${TRY_AGAIN}`,
        };
      case "AbortError":
        return {
          kind: "cancelled",
          message: `Passkey verification was cancelled. ${TRY_AGAIN}`,
        };
    }
  }

  // Server-side rejections come back from authClient as { error: { ... } }
  // — the caller passes them straight through. Status 429 = rate-limit.
  const errObj = err as { status?: number; message?: string } | undefined;
  if (errObj?.status === 429 || errObj?.message?.includes("429")) {
    return {
      kind: "rate-limited",
      message: "Too many attempts. Please wait a moment and try again.",
    };
  }
  if (errObj?.message) {
    return { kind: "server-rejected", message: `${errObj.message} ${TRY_AGAIN}` };
  }

  return {
    kind: "unknown",
    message: `Passkey verification failed. ${TRY_AGAIN}`,
  };
}
